import * as React from "react";

import { cva } from "class-variance-authority";
import type { Control, FieldValues, Path } from "react-hook-form";

import { Checkbox } from "@repo/ui/components/ui/checkbox";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@repo/ui/components/ui/form";
import { cn } from "@repo/ui/lib/utils";

export interface CheckboxFieldProps<T extends FieldValues = FieldValues> extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "size" | "type" | "className" | "onChange"
> {
  label?: string;
  description?: string;
  error?: string;
  success?: string;
  checked?: boolean; // standalone controlled
  onChange?: (checked: boolean) => void; // standalone controlled
  control?: Control<T>;
  name?: Path<T>;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  className?: string;
  checkboxClassName?: string;
  labelClassName?: string;
  descriptionClassName?: string;
  testId?: string;
}

const checkboxVariants = cva("border-brand-gray rounded", {
  variants: {
    size: {
      xs: "w-4 h-4", // 16px
      sm: "w-5 h-5", // 20px
      md: "w-6 h-6", // 24px
      lg: "w-7 h-7", // 28px
      xl: "w-8 h-8", // 32px
    },
  },
  defaultVariants: {
    size: "sm",
  },
});

function CheckboxFieldInner<T extends FieldValues = FieldValues>(
  {
    className,
    checkboxClassName,
    labelClassName,
    descriptionClassName,
    label,
    description,
    control,
    name,
    size = "sm",
    error,
    success,
    checked,
    onChange,
    testId,
    ...props
  }: CheckboxFieldProps<T>,
  ref: React.Ref<HTMLInputElement>,
) {
  const id = React.useId();

  // RHF mode
  if (control && name) {
    return (
      <FormField<T>
        control={control}
        name={name}
        render={({ field, fieldState }) => (
          <FormItem className={cn("", className)} data-testid={testId}>
            <FormControl>
              <div className="flex items-center gap-2">
                <Checkbox
                  key={`checkbox-${id}`}
                  id={id}
                  checked={!!field.value}
                  onCheckedChange={field.onChange}
                  onBlur={field.onBlur}
                  aria-invalid={!!fieldState.error}
                  className={cn("cursor-pointer", checkboxVariants({ size }), checkboxClassName)}
                  data-testid={`${testId ?? String(name)?.toLowerCase()}-chk`}
                />
                {label && (
                  <FormLabel
                    htmlFor={id}
                    className={cn("cursor-pointer select-none", labelClassName)}
                  >
                    {label}
                  </FormLabel>
                )}
                <input ref={ref} hidden {...props} />
              </div>
            </FormControl>
            {description && (
              <FormDescription className={cn("", descriptionClassName)}>
                {description}
              </FormDescription>
            )}
            <FormMessage />
          </FormItem>
        )}
      />
    );
  }

  // Standalone mode
  return (
    <div className={cn("", className)}>
      <div className="flex items-center gap-2">
        <Checkbox
          key={`checkbox-${id}`}
          id={id}
          checked={Boolean(checked)}
          onCheckedChange={(value) => onChange?.(value === true)}
          aria-invalid={!!error}
          className={cn("cursor-pointer", checkboxVariants({ size }), checkboxClassName)}
          data-testid={`${testId ?? String(label)?.toLowerCase()}-chk`}
        />
        {label && (
          <label htmlFor={id} className={cn("cursor-pointer select-none", labelClassName)}>
            {label}
          </label>
        )}
      </div>
      {description && <p className={cn("mt-1", descriptionClassName)}>{description}</p>}
      {error && <p className="text-xs text-red-500">{error}</p>}
      {success && <p className="text-xs text-green-500">{success}</p>}
    </div>
  );
}

export const CheckboxField = React.forwardRef(CheckboxFieldInner) as <
  T extends FieldValues = FieldValues,
>(
  props: CheckboxFieldProps<T> & { ref?: React.Ref<HTMLInputElement> },
) => React.ReactElement;

(CheckboxField as unknown as { displayName?: string }).displayName = "CheckboxField";
